
import React, { useEffect, ReactNode, Suspense, useRef, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import { db } from './services/db';
import { trackPageView } from './services/analytics';
import { setSentryUser, clearSentryUser, captureException as sentryCaptureException, captureMessage as sentryCaptureMessage } from './services/sentry';
import { lazyComponent } from './utils/lazyComponent';
import OptimizedLandingPage from './components/landing/OptimizedLandingPage';
import RouteSeoPolicy from './components/RouteSeoPolicy';
import BlogArticleModuleFallback, { BlogArticleModuleBoundary } from './components/blog/BlogArticleModuleFallback';
import ThemeRoutePolicy from './components/ThemeRoutePolicy';
import { shouldUseRedesignForSurface, useUiReleaseMode } from './config/uiRelease';
import { isKnownHarmlessBrowserDomError } from './utils/errorClassifiers';

// Lazy-loaded auth and legal components to reduce initial bundle size
const Auth = React.lazy(() => import('./components/Auth'));
const Legal = React.lazy(() => import('./components/Legal'));
const VerifyEmail = React.lazy(() => import('./components/VerifyEmail'));
const WelcomeBack = React.lazy(() => import('./components/WelcomeBack'));
const ResetPassword = React.lazy(() => import('./components/ResetPassword'));
const SSOHandler = React.lazy(() => import('./components/SSOHandler'));

// Lazy-loaded core components
// ⚡ Routed through lazyComponent() (not bare React.lazy) so a stalled chunk
// fetch rejects after a timeout instead of hanging forever — and emits a Sentry
// event. This is the safety net for reaching /dashboard or /admin via any path
// other than the login screen (direct URL, in-app nav), where Auth's login
// hand-off watchdog does not apply.
const Dashboard = lazyComponent(() => import('./components/Dashboard'), 'Dashboard');
const AdminPanel = lazyComponent(() => import('./components/admin/AdminPanel'), 'AdminPanel');
const HelpDesk = React.lazy(() => import('./components/helpdesk/HelpDesk'));
const Blog = React.lazy(() => import('./components/blog/Blog'));
const ChatWidget = React.lazy(() => import('./components/ChatWidget'));
const PublicChatReply = React.lazy(() => import('./components/PublicChatReply'));
const AboutPage = React.lazy(() => import('./components/AboutPage'));
const AccountDeletionPage = React.lazy(() => import('./components/AccountDeletionPage'));

// Homepage — keep the primary landing page in the initial app bundle so the
// first paint does not flash the generic Suspense loader before the hero.
const LandingPage = React.lazy(() => import('./components/landing/LandingPage'));
const MetadeckLanding = React.lazy(() => import('./components/landing/MetadeckLanding'));
const YouTubeSmmPreview = React.lazy(() => import('./components/youtube_smm/YouTubeSmmPreview'));
const YouTubeLanding = React.lazy(() => import('./components/landing/YouTubeLanding'));
const PricingPage = React.lazy(() => import('./components/PricingPage'));
const LandingRedesignPage = React.lazy(() => import('./components/landing/LandingRedesignPreview'));
const AdminRedesignPage = React.lazy(() => import('./components/admin/redesign/AdminRedesignPreview'));
// Der V2-Entwurf ist am 09.08.2026 auf Wunsch entfernt worden (Aussehen).
// Gesichert auf dem Branch `archive/landing-v2-20260809` — dort liegen auch
// LandingCompare (Vorher/Nachher-Slider) und die Lieferprotokoll-Grafik.
// `LandingRedesignV2.css` ist absichtlich geblieben: LandingRedesignV3 importiert sie.
// V3: derselbe Inhalt und dasselbe Farbsystem wie V2, Praesentation mit React Bits
// (components/reactbits/). Ebenfalls nur lokal — zieht gsap/ogl nach und laedt
// deshalb bewusst lazy, damit nichts davon im Bundle der Live-Startseite landet.
const LandingRedesignV3Page = React.lazy(() => import('./components/landing/LandingRedesignV3'));
const LandingV4Page = React.lazy(() => import('./components/landing/LandingV4'));
const PublicSpecialReleaseSurface = React.lazy(() => import('./components/landing/PublicSpecialReleaseSurface'));
const ReleaseControlPage = import.meta.env.DEV
    ? React.lazy(() => import('./components/ReleaseControlPage'))
    : null;
// Template-Kit: Katalog der Sektionsbausteine unter /template. Nur lokal.
// Anleitung: frontend/template/TEMPLATE.md
//
// 🔴 Die Bedingung gehoert um den `React.lazy`-Aufruf, nicht nur um die Route —
// genau wie oben bei `ReleaseControlPage`. Ein `React.lazy` auf oberster Ebene
// ist ein erreichbarer dynamischer Import, und Rollup baut den Chunk dann auch
// dann, wenn die Route im Produktionsbuild toter Code ist. Am 2026-08-09 genau
// so gemessen: `TemplateGallery-*.js` lag im `dist/`, obwohl `/template` dort
// nicht existiert — und mit ihm der Beams-Hero samt three.js.
const TemplateGalleryPage = import.meta.env.DEV
    ? React.lazy(() => import('./template/gallery/TemplateGallery'))
    : null;
import { brandConfig } from './config/brand';
import { WaveformRig } from './components/WaveformRig';

// Dynamic landing page loader — replaces 1,050+ explicit Route definitions
// The route map and individual components are loaded on-demand
const LandingPageLoader = React.lazy(() => import('./routes/LandingPageLoader'));
const HomepageSurface = React.lazy(() => import('./routes/HomepageSurface'));

// Loading fallback for lazy-loaded components
const PageLoader: React.FC = () => (
    <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a0a0a',
    }}>
        <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(255, 90, 10, 0.2)',
            borderTopColor: '#ff5a0a',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
        }} />
    </div>
);

// Branded, light loader used as the Suspense fallback for authenticated routes.
// The dark PageLoader above clashes hard with the white login screen — flashing
// black in the middle of the login → dashboard hand-off makes the app feel slow
// and broken. This matches Auth's success screen and Dashboard's own "Loading
// your workspace…" state so the transition stays visually continuous. With the
// Dashboard chunk prefetched on the login page, this usually doesn't even show.
const DashboardLoader: React.FC = () => (
    <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f3f4f6',
        gap: '24px',
    }}>
        <WaveformRig />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
                width: '40px', height: '40px', background: '#111',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
            }}>
                <span style={{ fontWeight: 900, color: brandConfig.primaryColor, fontSize: '18px' }}>{brandConfig.logoLetter}</span>
            </div>
            <span style={{ fontSize: '20px', fontWeight: 900, letterSpacing: '-0.05em', color: '#111827' }}>{brandConfig.name}</span>
        </div>
        <div style={{
            fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.2em', color: '#9ca3af',
        }}>
            Loading your workspace&hellip;
        </div>
    </div>
);

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/* Der Absturzbildschirm ist fuer viele Kunden die letzte Seite, die sie sehen -
   von dort muss ein Support-Weg abgehen, der nicht wieder durch die App fuehrt.
   Der Link traegt Fehlertext, Route und (falls lesbar) die Kontomail schon im
   Nachrichtentext, damit der Support nicht erst danach fragen muss. */
const buildErrorSupportWhatsAppUrl = (error: Error | null): string => {
    const number = brandConfig.supportWhatsAppNumber || '';
    const parts = ['Hi! I hit an error in Traffic Creator and need help.'];
    try {
        parts.push(`Page: ${window.location.pathname}`);
    } catch {
        // location darf den Support-Link nie verhindern.
    }
    const message = error?.message ? String(error.message).slice(0, 200) : '';
    if (message) parts.push(`Error: ${message}`);
    try {
        const user = db.getCurrentUser();
        if (user?.email) parts.push(`Account: ${user.email}`);
    } catch {
        // Kein Zugriff auf die Sitzung: Link geht trotzdem raus.
    }
    return `https://wa.me/${number}?text=${encodeURIComponent(parts.join('\n'))}`;
};

const DOM_RACE_REPORT_KEY = 'tc_dom_race_reported';

/* Die DOM-Rennen selbst bleiben in `ignoreErrors` — sie sind harmlos und kaemen
   massenhaft. Gemeldet wird stattdessen EINE Zaehlmeldung je Sitzung mit einem
   Namen, der durch keinen Filter faellt. Ohne sie ist nicht messbar, wie viele
   Kunden das trifft; genau daran ist der Fall vom 2026-08-22 tagelang
   vorbeigelaufen (Tickets: 0 Treffer, Logs: 0 Treffer, Realitaet: mindestens
   ein Kunde, der die Kampagnenanlage nicht durchlaufen konnte). */
const reportDomRaceRecovered = (error: unknown, occurrence: number): void => {
    try {
        if (sessionStorage.getItem(DOM_RACE_REPORT_KEY) === '1') return;
        sessionStorage.setItem(DOM_RACE_REPORT_KEY, '1');
    } catch {
        // Ohne Storage lieber einmal zu viel melden als gar nicht.
    }

    const message = error && typeof error === 'object' && 'message' in error
        ? String((error as { message?: unknown }).message ?? '')
        : String(error ?? '');

    // Chrome haengt der Wurzel `translated-ltr`/`translated-rtl` an, sobald die
    // Seitenuebersetzung laeuft — damit ist im Nachhinein belegbar, ob der
    // Uebersetzer im Spiel war, statt es nur zu vermuten.
    let translated = 'unknown';
    try {
        const cls = document.documentElement.className || '';
        translated = /\btranslated-(ltr|rtl)\b/.test(cls) ? 'yes' : 'no';
    } catch {
        // Kein DOM-Zugriff: bleibt 'unknown'.
    }

    sentryCaptureMessage('dom-race recovered by error boundary', 'warning', {
        tags: {
            alert_scope: 'dom_race_recovered',
            dom_race_kind: message.includes('insertBefore') ? 'insert'
                : message.includes('removeChild') ? 'remove'
                : 'other',
            page_translated: translated,
        },
        extra: {
            // Der Originaltext gehoert in `extra`, nicht in die Meldung: die
            // Filterliste prueft Meldung und Exception, nicht die Zusatzfelder.
            original_message: message.slice(0, 300),
            path: typeof window === 'undefined' ? '' : window.location.pathname,
            occurrence,
            html_lang: (() => {
                try { return document.documentElement.lang || ''; } catch { return ''; }
            })(),
        },
    });
};

const DOM_RECOVERY_RELOAD_KEY = 'tc_dom_recovery_reload';
const DOM_RECOVERY_RELOAD_THRESHOLD = 3;

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    props: ErrorBoundaryProps;
    state: ErrorBoundaryState = { hasError: false, error: null };
    _domRecoveryCount = 0;

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.props = props;
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        const errorMessage = error.message || (typeof error === 'string' ? error : '');
        const isChunkLoadError = 
            error.name === 'ChunkLoadError' ||
            errorMessage.includes('Failed to fetch dynamically imported module') ||
            errorMessage.includes('dynamically imported module') ||
            errorMessage.includes('Loading chunk');
            
        // Don't render the error boundary UI for chunk load errors, let componentDidCatch reload it
        if (isChunkLoadError || isKnownHarmlessBrowserDomError(error)) {
            return { hasError: false, error: null };
        }
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        const errorMessage = error.message || (typeof error === 'string' ? error : '');
        const isChunkLoadError = 
            error.name === 'ChunkLoadError' ||
            errorMessage.includes('Failed to fetch dynamically imported module') ||
            errorMessage.includes('dynamically imported module') ||
            errorMessage.includes('Loading chunk');

        if (isKnownHarmlessBrowserDomError(error)) {
            /* Swallowing lets React re-commit the tree. If the DOM keeps being
               rewritten under us (translator extensions do this continuously),
               re-committing never converges, so fall back to exactly one
               cache-distinct reload per session instead of spinning. */
            this._domRecoveryCount = (this._domRecoveryCount || 0) + 1;
            reportDomRaceRecovered(error, this._domRecoveryCount);
            if (this._domRecoveryCount > DOM_RECOVERY_RELOAD_THRESHOLD) {
                try {
                    if (sessionStorage.getItem(DOM_RECOVERY_RELOAD_KEY) === '1') return;
                    sessionStorage.setItem(DOM_RECOVERY_RELOAD_KEY, '1');
                } catch {
                    // Storage can be blocked; the single reload below still helps.
                }
                const recoveryUrl = new URL(window.location.href);
                recoveryUrl.searchParams.set('tc_dom_recovery', Date.now().toString());
                window.location.replace(recoveryUrl.toString());
            }
            return;
        }

        if (isChunkLoadError) {
            // Prevent infinite reload loops 
            const reloadCount = parseInt(sessionStorage.getItem('chunk_reload_count') || '0', 10);
            if (reloadCount < 2) {
                sessionStorage.setItem('chunk_reload_count', (reloadCount + 1).toString());
                console.warn('Chunk load error detected, bypassing cache to fetch latest version...');
                // Bust cache by appending a unique timestamp to the URL query string
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.set('v', Date.now().toString());
                window.location.replace(newUrl.toString());
                return;
            }
        }

        // Report to Sentry if not a simple chunk reload issue
        sentryCaptureException(error, {
            context: { source: 'ErrorBoundary' },
            extra: { componentStack: errorInfo.componentStack }
        });
    }

    componentDidMount() {
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const reason = event.reason;
            const message = reason?.message || (typeof reason === 'string' ? reason : '');
            
            const isChunkLoadError = 
                reason?.name === 'ChunkLoadError' ||
                message.includes('Failed to fetch dynamically imported module') ||
                message.includes('dynamically imported module') ||
                message.includes('Loading chunk');

            if (isChunkLoadError) {
                const reloadCount = parseInt(sessionStorage.getItem('chunk_reload_count') || '0', 10);
                if (reloadCount < 2) {
                    sessionStorage.setItem('chunk_reload_count', (reloadCount + 1).toString());
                    console.warn('Unhandled chunk load error detected, bypassing cache...');
                    const newUrl = new URL(window.location.href);
                    newUrl.searchParams.set('v', Date.now().toString());
                    window.location.replace(newUrl.toString());
                    return;
                }
            }

            sentryCaptureException(event.reason, { context: { source: 'ErrorBoundary' } });
        };
        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        (this as any)._unhandledRejectionHandler = handleUnhandledRejection;
    }

    componentWillUnmount() {
        if ((this as any)._unhandledRejectionHandler) {
            window.removeEventListener('unhandledrejection', (this as any)._unhandledRejectionHandler);
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center p-8">
                        <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
                        <p className="text-gray-600 mb-4">{this.state.error?.message}</p>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            <button
                                onClick={() => window.location.reload()}
                                className="bg-[#ff5a0a] text-white px-6 py-2 rounded font-bold"
                            >
                                <span>Reload page</span>
                            </button>
                            {brandConfig.supportWhatsAppNumber && (
                                <a
                                    href={buildErrorSupportWhatsAppUrl(this.state.error)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-[#25D366] text-white px-6 py-2 rounded font-bold inline-flex items-center gap-2"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                        <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.48-1.75-1.65-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.470 0 1.46 1.06 2.87 1.21 3.07.15.2 2.09 3.2 5.07 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35z" />
                                        <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.84 9.84 0 0 0 12.04 2zm0 18.13h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.36c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.7 8.23-8.24 8.23z" />
                                    </svg>
                                    <span>Get help on WhatsApp</span>
                                </a>
                            )}
                            <button
                                onClick={() => window.location.href = '/login'}
                                className="border border-gray-300 text-gray-700 px-6 py-2 rounded font-bold"
                            >
                                <span>Go to Login</span>
                            </button>
                        </div>
                        <p className="text-gray-400 text-xs mt-4">
                            <span>Nothing is lost — your account and campaigns are safe.</span>
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

/** Captures /ref/:code and /ref/u/:code → redirects to /signup?ref=:code */
const ReferralRedirect: React.FC = () => {
    const { code } = useParams();
    return <Navigate to={`/signup?ref=${code || ''}`} replace />;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode; adminOnly?: boolean }> = ({ children, adminOnly }) => {
    const user = db.getCurrentUser();

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (adminOnly && user.role !== 'admin') {
        return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
};

const LegalWrapper: React.FC = () => {
    const { type } = useParams<{ type: string }>();
    const navigate = useNavigate();

    const handleBack = () => {
        const user = db.getCurrentUser();
        if (user && (user.role === 'user' || user.role === 'admin')) {
            navigate('/dashboard');
        } else {
            navigate('/');
        }
    };

    return <Legal type={type as any} onBack={handleBack} />;
};

const App: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const uiReleaseMode = useUiReleaseMode();
    const [chatWidgetReady, setChatWidgetReady] = useState(false);
    const chatWidgetLoadedRef = useRef(false);
    const isLocalDesignPreview = import.meta.env.DEV
        && (location.pathname === '/landing-redesign-preview'
            || location.pathname.startsWith('/admin-redesign-preview')
            || location.pathname === '/release-control');

    useEffect(() => {
        document.documentElement.dataset.uiRelease = uiReleaseMode;
        if (window.gtag) {
            window.gtag('set', { ui_release: uiReleaseMode });
        }
    }, [uiReleaseMode]);

    // Initialize DB on mount
    useEffect(() => {
        if (isLocalDesignPreview) {
            return;
        }

        db.init();

        // Capture Google Ads Click ID (GCLID) for Value-Based Bidding
        const urlParams = new URLSearchParams(window.location.search);
        const clickId = urlParams.get('gclid');
        if (clickId) {
            localStorage.setItem('tgp_gclid', clickId);
        }
    }, [isLocalDesignPreview]);

    // Sync Sentry user context with auth state
    useEffect(() => {
        if (isLocalDesignPreview) {
            clearSentryUser();
            return;
        }

        const user = db.getCurrentUser();
        if (user) {
            setSentryUser({ id: user.id, email: user.email, role: user.role });
        } else {
            clearSentryUser();
        }
    }, [location, isLocalDesignPreview]);

    // Track Presence based on location
    useEffect(() => {
        if (isLocalDesignPreview) {
            return;
        }

        const user = db.getCurrentUser();
        if (user) {
            // Map path to "view" for legacy support in presence tracking
            const path = location.pathname.substring(1) || 'landing';
            db.trackPresence(path, user);

            // Set GA4 User-ID for cross-session/cross-device tracking
            if (window.gtag) {
                window.gtag('set', { user_id: user.id });
            }
        }
    }, [location, isLocalDesignPreview]);

    // Track SPA page views in GA4 on every route change
    useEffect(() => {
        if (isLocalDesignPreview) {
            return;
        }
        trackPageView(location.pathname + location.search);
    }, [location, isLocalDesignPreview]);

    useEffect(() => {
        if (isLocalDesignPreview) return;

        const handleAuthExpired = () => {
            // /welcome-back loggt sich selbst per Magic-Link ein. Eine alte,
            // abgelaufene Sitzung darf den Kunden dort nicht auf /login werfen,
            // bevor der Link ueberhaupt eingeloest wurde.
            if (location.pathname === '/login' || location.pathname === '/welcome-back') return;
            clearSentryUser();
            if (window.gtag) {
                window.gtag('set', { user_id: null });
            }
            const returnPath = `${location.pathname}${location.search}`;
            navigate(
                `/login?reason=session-expired&return=${encodeURIComponent(returnPath)}`,
                { replace: true },
            );
        };

        window.addEventListener('auth-expired', handleAuthExpired);
        return () => window.removeEventListener('auth-expired', handleAuthExpired);
    }, [isLocalDesignPreview, location.pathname, location.search, navigate]);

    // Keep route ownership with React Router. Calling navigate() from a global
    // Navigation API interceptor starts a second navigation inside the first in
    // Chromium, which skips the active View Transition and emits AbortError
    // noise. The routed Suspense fallbacks already provide continuous feedback.
    const navigateToRoot = () => {
        navigate('/');
        window.scrollTo(0, 0);
    };

    // ── Chunk-Reload-Counter reset ──
    // The ErrorBoundary bumps sessionStorage['chunk_reload_count'] on every
    // ChunkLoadError and gives up after 2 reloads. Without a reset, a user who
    // hits 2 chunk failures early in a session gets dead clicks forever
    // (subsequent lazy loads silently fail). Reset on every successful route
    // change so the counter only tracks *consecutive* failures.
    useEffect(() => {
        sessionStorage.removeItem('chunk_reload_count');
    }, [location.pathname]);

    // Keep public landing pages focused on the hero first. The lightweight
    // WhatsApp launcher is deferred until idle, while dashboard/support pages
    // keep it immediately available.
    useEffect(() => {
        if ((window as any).__PRERENDER__) {
            setChatWidgetReady(false);
            return;
        }

        if (isLocalDesignPreview) {
            setChatWidgetReady(false);
            return;
        }

        const loadImmediately =
            location.pathname.startsWith('/dashboard') ||
            location.pathname.startsWith('/admin') ||
            location.pathname.startsWith('/support/live');

        if (loadImmediately) {
            chatWidgetLoadedRef.current = true;
            setChatWidgetReady(true);
            return;
        }

        if (chatWidgetLoadedRef.current) {
            setChatWidgetReady(true);
            return;
        }

        setChatWidgetReady(false);
        let idleId: number | null = null;
        const showChatWidget = () => {
            chatWidgetLoadedRef.current = true;
            setChatWidgetReady(true);
        };
        const timeoutId = window.setTimeout(() => {
            if ('requestIdleCallback' in window) {
                idleId = window.requestIdleCallback(showChatWidget, { timeout: 2500 });
            } else {
                showChatWidget();
            }
        }, 3000);

        return () => {
            window.clearTimeout(timeoutId);
            if (idleId !== null && 'cancelIdleCallback' in window) {
                window.cancelIdleCallback(idleId);
            }
        };
    }, [location.pathname, isLocalDesignPreview]);

    const handleLogin = (role: 'user' | 'admin') => {
        // ⚠️ Plain navigate(): the destination
        // (Dashboard/AdminPanel) is React.lazy, so its render suspends while the
        // chunk loads. document.startViewTransition() snapshots the current page
        // (Auth's white success screen) and freezes it as an overlay until the
        // callback's DOM update settles — but navigate() returns before the lazy
        // chunk resolves, so the frozen success-screen screenshot can stay on top
        // indefinitely and the user never reaches the dashboard. The Suspense
        // fallback (branded loader) handles the chunk-load wait correctly instead.
        navigate(role === 'admin' ? '/admin' : '/dashboard');
        window.scrollTo(0, 0);
    };

    const handleLogout = () => {
        // Clear GA4 User-ID on logout
        if (window.gtag) {
            window.gtag('set', { user_id: null });
        }
        db.logout();
    };

    const handleBack = () => {
        navigateToRoot();
    };

    return (
        <ErrorBoundary>
            <RouteSeoPolicy />
            <ThemeRoutePolicy />
            <Suspense fallback={<BlogArticleModuleFallback><PageLoader /></BlogArticleModuleFallback>}>
                <Routes>
                    <Route path="/" element={
                        brandConfig.id === 'trafficcreator'
                        ? <HomepageSurface
                            uiReleaseMode={uiReleaseMode}
                            onLogin={() => navigate('/login')}
                            onNavigate={(page) => navigate(`/${page}`)}
                          />
                        : <MetadeckLanding onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                    } />

                    {/* Design previews are local-only and never ship as public bypass routes. */}
                    {import.meta.env.DEV && (
                        <>
                            <Route path="/metadeck-preview" element={
                                <MetadeckLanding onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                            } />
                            <Route path="/landing-new" element={
                                <OptimizedLandingPage onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                            } />
                            {brandConfig.id === 'trafficcreator' && (
                                <>
                                    <Route path="/landing-redesign-preview" element={<LandingRedesignPage previewMode />} />
                                    <Route path="/landing-v3" element={<LandingRedesignV3Page />} />
                                    <Route path="/landing-v4" element={<LandingV4Page />} />
                                    {TemplateGalleryPage && (
                                        <>
                                            <Route path="/template" element={<TemplateGalleryPage />} />
                                            {/* Einzelne Variante bildschirmfuellend — so laesst sich das
                                                Verhalten bei anderen Breiten am echten Viewport pruefen. */}
                                            <Route path="/template/:variantId" element={<TemplateGalleryPage />} />
                                        </>
                                    )}
                                </>
                            )}
                            {/* Splat: AdminPanel mounts its own nested <Routes> for every admin page. */}
                            <Route path="/admin-redesign-preview/*" element={<AdminRedesignPage />} />
                            <Route path="/youtube-smm-preview" element={<YouTubeSmmPreview />} />
                            <Route path="/youtube-preview" element={
                                <YouTubeLanding onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                            } />
                        </>
                    )}

                    {ReleaseControlPage && (
                        <Route path="/release-control" element={<ReleaseControlPage />} />
                    )}

                    <Route path="/youtube" element={
                        <PublicSpecialReleaseSurface mode={brandConfig.id === 'trafficcreator' ? uiReleaseMode : 'legacy'} surface="youtube">
                            <YouTubeLanding onLogin={() => navigate('/login')} onNavigate={(page) => navigate(`/${page}`)} />
                        </PublicSpecialReleaseSurface>
                    } />

                    {brandConfig.id === 'trafficcreator' && (
                        <Route path="/pricing" element={
                            <PublicSpecialReleaseSurface mode={uiReleaseMode} surface="pricing">
                                <PricingPage />
                            </PublicSpecialReleaseSurface>
                        } />
                    )}

                    {/* Auth Routes */}
                    <Route path="/login" element={<Auth view="login" onLogin={handleLogin} onNavigate={(v) => navigate(`/${v}`)} />} />
                    <Route path="/signup" element={<Auth view="signup" onLogin={handleLogin} onNavigate={(v) => navigate(`/${v}`)} />} />
                    <Route path="/forgot" element={<Auth view="forgot" onLogin={handleLogin} onNavigate={(v) => navigate(`/${v}`)} />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/welcome-back" element={<WelcomeBack />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/sso" element={<SSOHandler />} />

                    {/* Referral link redirects → signup with ref code */}
                    <Route path="/ref/:code" element={<ReferralRedirect />} />
                    <Route path="/ref/u/:code" element={<ReferralRedirect />} />
                    <Route path="/register" element={<Navigate to={`/signup${window.location.search}`} replace />} />
                    <Route path="/support/live/:ticketId" element={<PublicChatReply />} />


                    {/* Protected Routes — wrapped in their own Suspense so the
                        lazy Dashboard/Admin chunk shows the light, branded loader
                        instead of the dark app-level PageLoader (no black flash
                        right after the white login screen). */}
                    <Route path="/dashboard/*" element={
                        <ProtectedRoute>
                            <Suspense fallback={<DashboardLoader />}>
                                <Dashboard onLogout={handleLogout} onNavigate={(page) => navigate(`/${page}`)} />
                            </Suspense>
                        </ProtectedRoute>
                    } />

                    <Route path="/admin/*" element={
                        <ProtectedRoute adminOnly>
                            <Suspense fallback={<DashboardLoader />}>
                                <AdminPanel onLogout={handleLogout} />
                            </Suspense>
                        </ProtectedRoute>
                    } />

                    {/* Other Pages */}
                    {brandConfig.id === 'trafficcreator' && (
                        <>
                            <Route path="/helpdesk" element={<HelpDesk onBack={handleBack} />} />
                            <Route path="/helpdesk/:category" element={<HelpDesk onBack={handleBack} />} />
                            <Route path="/helpdesk/:category/:slug" element={<HelpDesk onBack={handleBack} />} />
                            <Route path="/blog" element={<Blog onBack={handleBack} />} />
                            <Route path="/blog/:slug" element={<BlogArticleModuleBoundary><Blog onBack={handleBack} /></BlogArticleModuleBoundary>} />
                            <Route path="/:lang/blog" element={<Blog onBack={handleBack} />} />
                            <Route path="/:lang/blog/:slug" element={<BlogArticleModuleBoundary><Blog onBack={handleBack} /></BlogArticleModuleBoundary>} />
                            
                            {/* Legal Routes */}
                            <Route path="/legal/:type" element={<LegalWrapper />} />
                            <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                            <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
                            <Route path="/impressum" element={<Navigate to="/legal/impressum" replace />} />
                            <Route path="/agb" element={<Navigate to="/legal/agb" replace />} />
                            <Route path="/refund" element={<Navigate to="/legal/refund" replace />} />
                            <Route path="/delivery" element={<Navigate to="/legal/delivery" replace />} />
                            <Route path="/account-deletion" element={<AccountDeletionPage />} />

                            {/* About Page */}
                            <Route path="/about" element={<AboutPage onBack={handleBack} />} />

                            {import.meta.env.DEV && (
                                <Route path="/pricing-sample" element={
                                    <React.Suspense fallback={<PageLoader />}>
                                        {React.createElement(React.lazy(() => import('./components/landing/SamplePricing')))}
                                    </React.Suspense>
                                } />
                            )}
                        </>
                    )}

                    {/* 
                     * Dynamic Landing Pages — replaces 1,050+ explicit route definitions.
                     * The LandingPageLoader dynamically imports the route map and resolves
                     * the correct landing page component based on the current URL pathname.
                     * This moves ~330KB of route declarations out of the main bundle.
                     */}
                    { brandConfig.id === 'trafficcreator' && <Route path="*" element={<LandingPageLoader />} /> }
                    { brandConfig.id === 'metadeck' && <Route path="*" element={<Navigate to="/" replace />} /> }
                </Routes>
            </Suspense>
            {chatWidgetReady && !isLocalDesignPreview && (
                <Suspense fallback={null}>
                    <ChatWidget />
                </Suspense>
            )}
        </ErrorBoundary>
    );
};

export default App;
